import { Box, Divider, Stack, Typography } from '@mui/material';
import { Provider, type AwsVpcConnectivityConfig } from '@repo/api-gw-sdk';
import { useEffect, useMemo, useState } from 'react';

import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { AwsCustomizeNetworkBottomDialog } from './awsCustomizeNetworkBottomDialog';
import { DeleteRestoreServerConfigDialog } from './deleteRestoreServerConfigDialog';
import type { RestoreAccountConfigState } from './restoreAccountConfigFlow';
import { SubnetSectionInfo } from './subnetSectionInfo';
import {
  cloudAccountConfigurationAwsResourceTypes,
  cloudAccountConfigurationAwsResourceTypesTitles,
} from './types';
import { VpcSelectionDialog } from './vpcSelectionDialog';

export const AwsRestoreAccountConfigVpcSelection = (
  props: StepProps<RestoreAccountConfigState>
) => {
  const dal = useDAL();
  const [addCustomVPCSettingsButtonEl, setAddCustomVPCSettingsButtonEl] =
    useState<HTMLElement | null>(null);

  const [configForDeletion, setConfigForDeletion] = useState<
    AwsVpcConnectivityConfig | undefined
  >(undefined);
  const [showVpcSelectionDialog, setShowVpcSelectionDialog] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<
    AwsVpcConnectivityConfig | undefined
  >();
  const [editedConfig, setEditedConfig] = useState<
    AwsVpcConnectivityConfig | undefined
  >();

  const { data: availabilityZonesBody } = dal.restore.availabilityZones.useList(
    props.wizardState.account.id,
    selectedConfig?.region
  );

  const { data: securityGroupsBody } = dal.restore.securityGroups.useList(
    props.wizardState.account.id,
    selectedConfig?.region,
    selectedConfig?.vpc
  );

  const securityGroupsMap: Record<string, string> = useMemo(() => {
    return Object.fromEntries(
      securityGroupsBody?.securityGroups?.map((sg) => [sg.id, sg.name]) || []
    );
  }, [securityGroupsBody]);

  const regionConfigurations = Object.entries(
    (props.wizardState.config.config.aws?.vpcConfigs || []).reduce<
      Record<string, AwsVpcConnectivityConfig[]>
    >((agg, val) => {
      agg[val.region] = agg[val.region] || [];
      agg[val.region].push(val);
      return agg;
    }, {})
  ).sort((a, b) => a[0].localeCompare(b[0]));

  useEffect(() => {
    if (
      !selectedConfig ||
      !props.wizardState.config.config.aws?.vpcConfigs?.includes(selectedConfig)
    ) {
      setSelectedConfig(regionConfigurations[0]?.[1][0]);
    }
  }, [
    props.wizardState.config.config.aws?.vpcConfigs,
    regionConfigurations,
    selectedConfig,
  ]);

  return (
    <StepContainer
      hideStepper
      onBackClick={props.back}
      canGoNext={() => true}
      nextButtonText={'Save Changes'}
      backButtonText='Cancel'
      onNextClick={() => props.finish()}
      sx={{ height: '100%' }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack className='px-[50px]' direction='row' flexGrow={1}>
          <Stack className='w-[280px]' gap='40px'>
            {!!props.wizardState.config.config.aws?.vpcConfigs?.length && (
              <Stack gap='40px'>
                <Typography>Customized VPCs</Typography>
                <Stack gap='24px'>
                  {regionConfigurations.map(([regionName, configs], index) => (
                    <Stack key={index} gap='24px'>
                      <Typography
                        className='uppercase'
                        variant='body2'
                        sx={{
                          fontSize: '12px',
                        }}
                      >
                        {regionName}
                      </Typography>
                      {configs.map((config) => (
                        <Stack
                          key={config.vpc}
                          justifyContent='space-between'
                          direction='row'
                          alignItems='center'
                          className='pr-[22px] cursor-pointer'
                          sx={{
                            ...(selectedConfig === config
                              ? { borderRight: '2px solid black' }
                              : { borderRight: '2px solid transparent' }),
                          }}
                          onClick={() => setSelectedConfig(config)}
                        >
                          <Typography>{config.vpc}</Typography>
                          <OptionMenu
                            options={[
                              {
                                id: 'delete-config',
                                text: 'Delete',
                                menuItemProps: {
                                  onClick: () => setConfigForDeletion(config),
                                },
                              },
                            ]}
                          />
                        </Stack>
                      ))}
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            )}
            <Box>
              <ButtonWithIcon
                ref={setAddCustomVPCSettingsButtonEl}
                variant='outlined'
                icon='material-symbols-add-rounded'
                text={
                  props.wizardState.config.config.aws?.vpcConfigs?.length
                    ? 'Customize Another VPC'
                    : 'Customize VPC'
                }
                onClick={() => setShowVpcSelectionDialog(true)}
              />
            </Box>
            {!!props.wizardState.config.config.aws?.vpcConfigs?.length && (
              <>
                <Divider className='mt-[20px] ml-[-100px]' />
                <Typography
                  className='w-[208px]'
                  fontWeight={'300'}
                  lineHeight={'20px'}
                >
                  For all other VPCs, Eon chooses a suitable subnet and security
                  group.
                </Typography>
              </>
            )}
            <DeleteRestoreServerConfigDialog
              name={configForDeletion?.vpc || ''}
              provider={Provider.Aws}
              isOpen={!!configForDeletion}
              onCancel={() => setConfigForDeletion(undefined)}
              onConfirm={() => {
                setConfigForDeletion(undefined);
                props.setWizardState((state) => ({
                  ...state,
                  config: {
                    ...state.config,
                    config: {
                      ...state.config.config,
                      aws: {
                        ...state.config.config.aws,
                        vpcConfigs: state.config.config.aws?.vpcConfigs?.filter(
                          (x) => x !== configForDeletion
                        ),
                      },
                    },
                  },
                }));
              }}
            />
            {showVpcSelectionDialog && (
              <VpcSelectionDialog
                config={props.wizardState.config.config.aws?.vpcConfigs || []}
                anchorEl={addCustomVPCSettingsButtonEl}
                accountId={props.wizardState.account.id}
                onClose={(regionName, vpc) => {
                  setShowVpcSelectionDialog(false);
                  if (
                    !regionName ||
                    !vpc ||
                    props.wizardState.config.config.aws?.vpcConfigs?.some(
                      (x) => x.region === regionName && x.vpc === vpc
                    )
                  ) {
                    return;
                  }

                  const config: AwsVpcConnectivityConfig = {
                    region: regionName,
                    vpc,
                  };

                  props.setWizardState((state) => ({
                    ...state,
                    config: {
                      ...state.config,
                      config: {
                        ...state.config.config,
                        aws: {
                          ...state.config.config.aws,
                          vpcConfigs: [
                            ...(state.config.config.aws?.vpcConfigs || []),
                            config,
                          ],
                        },
                      },
                    },
                  }));
                  setSelectedConfig(config);
                  setEditedConfig(config);
                }}
              />
            )}
          </Stack>
          {!!props.wizardState.config.config.aws?.vpcConfigs?.length && (
            <Divider flexItem orientation='vertical' />
          )}
          {selectedConfig && (
            <Stack className='ml-[40px]' gap='40px' flexGrow={1}>
              <Stack direction='row' justifyContent='space-between'>
                <Stack direction='row' gap='4px'>
                  <Typography component='span'>{`Restore server in`}</Typography>
                  <Typography component='span' className='font-semibold'>
                    {selectedConfig.vpc}
                  </Typography>
                  <Typography component='span'>{`will use these settings:`}</Typography>
                </Stack>
                <ButtonWithIcon
                  variant='outlined'
                  icon='material-symbols-stylus-outline'
                  text='Edit VPC'
                  onClick={() => setEditedConfig(selectedConfig)}
                />
              </Stack>
              <Stack direction='row' gap='100px'>
                <SubnetSectionInfo
                  privateSubnetEnabled={
                    selectedConfig?.privateSubnetEnabled ?? false
                  }
                  onPrivateSubnetChange={(checked) => {
                    if (!selectedConfig) return;
                    const updatedConfig: AwsVpcConnectivityConfig = {
                      ...selectedConfig,
                      privateSubnetEnabled: checked,
                    };
                    props.setWizardState((state) => ({
                      ...state,
                      config: {
                        ...state.config,
                        config: {
                          ...state.config.config,
                          aws: {
                            ...state.config.config.aws,
                            vpcConfigs:
                              state.config.config.aws?.vpcConfigs?.map((x) =>
                                x === selectedConfig ? updatedConfig : x
                              ),
                          },
                        },
                      },
                    }));
                    setSelectedConfig(updatedConfig);
                  }}
                />
                <Stack
                  flexGrow={1}
                  gap='24px'
                  divider={<Divider sx={{ borderStyle: 'dashed' }} />}
                >
                  {(availabilityZonesBody?.availabilityZones || [])
                    .sort()
                    .map((az) => {
                      const subnetId =
                        selectedConfig.subnetsPerAvailabilityZone?.find(
                          (y) => y.availabilityZone === az
                        )?.subnetId;

                      return (
                        <Stack key={az}>
                          <Stack direction='row' alignItems='center' gap='40px'>
                            <Typography className='w-[150px]'>{az}</Typography>
                            {subnetId && <Typography>{subnetId}</Typography>}
                            {!subnetId && (
                              <Typography
                                color={'var(--mui-palette-text-disabled)'}
                              >
                                Default subnet
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                      );
                    })}
                </Stack>
              </Stack>
              <Divider className='my-[20px]' />
              <Stack direction='row' gap='100px'>
                <Stack gap='36px' className='w-[240px]'>
                  <Typography className='font-semibold'>
                    Security groups
                  </Typography>
                  <Typography lineHeight={'20px'} fontWeight={300}>
                    {`The restore server and restored RDS instance will use the specified security groups. Make sure outbound access is open, and that the RDS and restore server security groups are open to each other.`}
                  </Typography>
                </Stack>
                <Stack
                  flexGrow={1}
                  gap='24px'
                  divider={<Divider sx={{ borderStyle: 'dashed' }} />}
                >
                  {cloudAccountConfigurationAwsResourceTypes.map((key) => {
                    const securityGroupIds =
                      selectedConfig.securityGroups?.[key];
                    const hasSecurityGroups =
                      securityGroupIds !== undefined &&
                      securityGroupIds.length > 0;
                    const title =
                      cloudAccountConfigurationAwsResourceTypesTitles[key];

                    return (
                      <Stack key={key}>
                        <Stack direction='row' alignItems='center' gap='40px'>
                          <Typography className='w-[150px]'>{title}</Typography>
                          {hasSecurityGroups ? (
                            <Typography>
                              {securityGroupIds
                                .map((id) => securityGroupsMap[id] || id)
                                .join(', ')}
                            </Typography>
                          ) : (
                            <Typography
                              color={'var(--mui-palette-text-disabled)'}
                            >
                              Default security group
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Stack>
      <AwsCustomizeNetworkBottomDialog
        accountId={props.wizardState.account.id}
        config={editedConfig}
        availabilityZones={availabilityZonesBody?.availabilityZones || []}
        onCancel={() => setEditedConfig(undefined)}
        onSave={(updatedConfig) => {
          props.setWizardState((state) => ({
            ...state,
            config: {
              ...state.config,
              config: {
                ...state.config.config,
                aws: {
                  ...state.config.config.aws,
                  vpcConfigs: state.config.config.aws?.vpcConfigs?.map((x) =>
                    x === editedConfig ? updatedConfig : x
                  ),
                },
              },
            },
          }));
          setSelectedConfig(updatedConfig);
          setEditedConfig(undefined);
        }}
      />
    </StepContainer>
  );
};
